/**
 * Security utilities for XSS protection and data sanitization
 */

// HTML entities to escape XSS attacks
const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
}

/**
 * Escape HTML entities to prevent XSS attacks
 * @param {string} text - The text to escape
 * @returns {string} - The escaped text
 */
export function escapeHtml(text) {
  if (typeof text !== 'string') {
    return text
  }
  return text.replace(/[&<>"'`=/]/g, (match) => HTML_ENTITIES[match])
}

/**
 * Sanitize user input by removing potentially dangerous characters
 * @param {string} input - The input to sanitize
 * @param {object} options - Sanitization options
 * @returns {string} - The sanitized input
 */
export function sanitizeInput(input, options = {}) {
  if (typeof input !== 'string') {
    return input
  }
  
  const {
    allowHtml = false,
    maxLength = null,
    removeScripts = true,
    removeEvents = true,
  } = options

  let sanitized = input

  // Remove script tags and their content
  if (removeScripts) {
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
  }

  // Remove event handlers (onclick, onload, etc.)
  if (removeEvents) {
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^>\s]+/gi, '')
  }

  // Remove potentially dangerous tags if HTML is not allowed
  if (!allowHtml) {
    sanitized = escapeHtml(sanitized)
  }

  // Trim to max length if specified
  if (maxLength && sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength)
  }

  return sanitized.trim()
}

/**
 * Validate and sanitize URL to prevent malicious redirects
 * @param {string} url - The URL to validate
 * @param {Array} allowedDomains - Array of allowed domains
 * @returns {string|null} - The sanitized URL or null if invalid
 */
export function sanitizeUrl(url, allowedDomains = []) {
  if (typeof url !== 'string') {
    return null
  }

  // Remove dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:']
  const lowerUrl = url.toLowerCase()
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return null
    }
  }

  // Allow relative URLs
  if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
    return url
  }

  // Allow same-origin URLs
  if (url.startsWith('#') || url.startsWith('?')) {
    return url
  }

  // Check allowed domains for absolute URLs
  if (allowedDomains.length > 0) {
    try {
      const urlObj = new URL(url)
      const isAllowed = allowedDomains.some(domain => 
        urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain)
      )
      if (!isAllowed) {
        return null
      }
    } catch (e) {
      return null
    }
  }

  return url
}

/**
 * Validate file upload to prevent malicious files
 * @param {File} file - The file to validate
 * @param {object} options - Validation options
 * @returns {object} - Validation result with isValid and error
 */
export function validateFileUpload(file, options = {}) {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  } = options

  // Check if file exists
  if (!file) {
    return { isValid: false, error: 'No file selected' }
  }

  // Check file size
  if (file.size > maxSize) {
    return { 
      isValid: false, 
      error: `File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB` 
    }
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return { 
      isValid: false, 
      error: `File type ${file.type} is not allowed` 
    }
  }

  // Check file extension
  const fileName = file.name.toLowerCase()
  const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext))
  if (!hasValidExtension) {
    return { 
      isValid: false, 
      error: `File extension must be one of: ${allowedExtensions.join(', ')}` 
    }
  }

  // Additional security check: ensure MIME type matches extension
  const extension = fileName.substring(fileName.lastIndexOf('.'))
  const mimeTypeMap = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.webp': 'image/webp'
  }

  if (mimeTypeMap[extension] && mimeTypeMap[extension] !== file.type) {
    return {
      isValid: false,
      error: 'File type does not match file extension'
    }
  }

  return { isValid: true, error: null }
}

/**
 * Generate a Content Security Policy (CSP) header value
 * @param {object} options - CSP options
 * @returns {string} - CSP header value
 */
export function generateCSP(options = {}) {
  const {
    scriptSrc = ["'self'"],
    styleSrc = ["'self'", "'unsafe-inline'"],
    imgSrc = ["'self'", "data:", "https:"],
    fontSrc = ["'self'", "https:", "data:"],
    connectSrc = ["'self'"],
    frameSrc = ["'none'"],
    objectSrc = ["'none'"],
    mediaSrc = ["'self'"],
    childSrc = ["'none'"],
  } = options

  const directives = {
    'default-src': ["'self'"],
    'script-src': scriptSrc,
    'style-src': styleSrc,
    'img-src': imgSrc,
    'font-src': fontSrc,
    'connect-src': connectSrc,
    'frame-src': frameSrc,
    'object-src': objectSrc,
    'media-src': mediaSrc,
    'child-src': childSrc,
    'base-uri': ["'self'"],
    'form-action': ["'self'"],
    'frame-ancestors': ["'none'"],
  }

  return Object.entries(directives)
    .map(([directive, sources]) => `${directive} ${sources.join(' ')}`)
    .join('; ')
}

/**
 * Validate password strength beyond basic requirements
 * @param {string} password - The password to validate
 * @returns {object} - Validation result with score and feedback
 */
export function validatePasswordStrength(password) {
  let score = 0
  const feedback = []

  if (!password) {
    return { score: 0, strength: 'Very Weak', feedback: ['Password is required'] }
  }

  // Length check
  if (password.length >= 8) score += 1
  if (password.length >= 12) score += 1
  if (password.length < 8) feedback.push('Use at least 8 characters')

  // Character variety
  if (/[a-z]/.test(password)) score += 1
  else feedback.push('Include lowercase letters')

  if (/[A-Z]/.test(password)) score += 1
  else feedback.push('Include uppercase letters')

  if (/[0-9]/.test(password)) score += 1
  else feedback.push('Include numbers')

  if (/[^A-Za-z0-9]/.test(password)) score += 1
  else feedback.push('Include special characters')

  // Common patterns check
  if (/(.)\1{2,}/.test(password)) {
    score -= 1
    feedback.push('Avoid repeated characters')
  }

  // Dictionary words (basic check)
  const commonWords = ['password', 'admin', 'user', 'login', '123456', 'qwerty']
  if (commonWords.some(word => password.toLowerCase().includes(word))) {
    score -= 1
    feedback.push('Avoid common words')
  }

  const strengthLevels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong']
  const strength = strengthLevels[Math.min(Math.max(score - 1, 0), 4)]

  return {
    score: Math.max(score, 0),
    strength,
    feedback: feedback.length > 0 ? feedback : ['Password looks good!']
  }
}

/**
 * Rate limiting utility for preventing brute force attacks
 */
export class RateLimiter {
  constructor(maxAttempts = 5, windowMs = 15 * 60 * 1000) { // 5 attempts per 15 minutes
    this.maxAttempts = maxAttempts
    this.windowMs = windowMs
    this.attempts = new Map()
  }

  isBlocked(identifier) {
    const attempts = this.attempts.get(identifier) || []
    const now = Date.now()
    
    // Clean old attempts
    const recentAttempts = attempts.filter(time => now - time < this.windowMs)
    this.attempts.set(identifier, recentAttempts)
    
    return recentAttempts.length >= this.maxAttempts
  }

  recordAttempt(identifier) {
    const attempts = this.attempts.get(identifier) || []
    attempts.push(Date.now())
    this.attempts.set(identifier, attempts)
  }

  reset(identifier) {
    this.attempts.delete(identifier)
  }

  getTimeUntilReset(identifier) {
    const attempts = this.attempts.get(identifier) || []
    if (attempts.length === 0) return 0
    
    const oldestAttempt = Math.min(...attempts)
    const timeElapsed = Date.now() - oldestAttempt
    return Math.max(0, this.windowMs - timeElapsed)
  }
}